// pages/WishlistPage.tsx
import React from 'react';
import { useWishlistProducts, useClearWishlist } from '@/hooks/useWishlist';
import { Heart, Trash2, Package} from 'lucide-react';
import { Link } from 'react-router-dom';
import WishlistSkeleton from '@/components/ui/WishlistSkeleton';
import { Helmet } from 'react-helmet-async';
import ProductCard from '@/components/common/ProductCard';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

const WishlistPage = () => {
  const { data: wishlistProducts = [], isLoading } = useWishlistProducts();
  const clearWishlist = useClearWishlist();
  const [clear, setClear] = React.useState(false);


  const handleClearWishlist = () => {
    setClear(true);
  };

  if (isLoading) {
    return <WishlistSkeleton />;
  }

  if (wishlistProducts.length === 0) {
    return (
      <>
        <Helmet > <title>My Wishlist | Bookstore</title> </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-primary mb-3">Your Wishlist is Empty</h2>
            <p className="text-primary-800 mb-8">
              Start adding products you love and we'll save them here for you
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-2xl font-medium hover:bg-primary-700 transition-colors"
            >
              <Package className="w-5 h-5" />
              Browse Products
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Wishlist | Bookstore</title>
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="container-custom max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">My Wishlist</h1>
              <p className="text-primary-800">
                {wishlistProducts.length} {wishlistProducts.length === 1 ? 'item' : 'items'} saved
              </p>
            </div>

            {wishlistProducts.length > 0 && (
              <button
                onClick={handleClearWishlist}
                disabled={clearWishlist.isPending}
                className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-red-100 rounded-xl transition-colors font-medium disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlistProducts.map((product) => {
              return (
                  <ProductCard
                    key={product.id}
                    product={product}              
                  />
              );
            })}
          </div>
        </div>
      </div>
      <ConfirmDialog
            isOpen = {clear}
      title="Clear Wishlist"
      message="Are you sure you want to clear your entire wishlist? This action cannot be undone."
      onConfirm={() => {
        clearWishlist.mutate();
      }}
      onClose={() => setClear(false)}
    />
    </>
  );
};

export default WishlistPage;